import { useState } from "react"
import { useAuth } from "@clerk/clerk-react"
import { motion } from "framer-motion"
import { Check, Download, Video, Loader2, Calendar } from "lucide-react"
import { useStudioStore } from "@/store/studio"
import { publishProject } from "@/lib/api"
import { cn } from "@/lib/utils"

export default function PublishView() {
  const { getToken } = useAuth()
  const store = useStudioStore()
  const [title, setTitle] = useState(store.title)
  const [description, setDescription] = useState(store.logline + "\n\nCreated with Atlas by HillPrime Innovations.")
  const [visibility, setVisibility] = useState("Public")
  const [audience, setAudience] = useState("Not made for kids")
  const [scheduleMode, setScheduleMode] = useState<"now" | "schedule">("now")
  const [scheduleDate, setScheduleDate] = useState("")
  const [publishing, setPublishing] = useState(false)

  const ready = Boolean(
    store.identity &&
    store.capabilities.youtube &&
    store.youtubeConnected &&
    store.masterArtifact?.id
  )

  const handlePublish = async () => {
    const token = await getToken()
    if (!token || !store.cloudProjectId) return
    ;(window as unknown as Record<string, unknown>).__atlas_token__ = token
    setPublishing(true)
    try {
      await publishProject(store.cloudProjectId, {
        title,
        description,
        visibility: visibility.toLowerCase(),
        audience: audience.toLowerCase().replace(/ /g, "_"),
        aiGenerated: true,
        schedule: scheduleMode === "schedule" ? scheduleDate : null,
      }, token)
      store.setPublished(true)
      store.setStage(5)
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Release submitted!" } }))
    } catch (e) {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: (e as Error).message || "Publish failed" } }))
    } finally {
      setPublishing(false)
    }
  }

  const handleConnectVideo = async () => {
    const token = await getToken()
    if (!token) return
    ;(window as unknown as Record<string, unknown>).__atlas_token__ = token
    try {
      const res = await fetch("https://atlas-cloud.ogunwobiphilip.workers.dev/v1/integrations/youtube/authorize", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ returnTo: "/studio/publish" }),
      })
      const data = await res.json() as { authorizationUrl?: string }
      if (data.authorizationUrl) location.assign(data.authorizationUrl)
    } catch {
      window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "YouTube connection failed" } }))
    }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow mb-1 text-[#f59e0b]">Stage 5 · Publish</p>
          <h1 className="heading-display text-3xl">Meet your audience.</h1>
          <p className="text-sm text-[#9ca3af] mt-1">Package, schedule and release—with a downloadable copy always yours.</p>
        </div>
        <button
          onClick={() => {
            const a = document.createElement("a")
            a.href = `/v1/downloads/${store.masterArtifact?.id}`
            a.download = `atlas-master-${store.masterArtifact?.id}.mp4`
            a.click()
          }}
          disabled={!store.masterArtifact?.id}
          className="btn-subtle text-xs"
        >
          <Download className="w-3.5 h-3.5" /> Download master
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Composer */}
        <div className="surface p-4 sm:p-6">
          {/* Platform tabs */}
          <div className="flex items-center gap-2 mb-6 border-b border-[#1e2130] pb-3">
            <button className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-[#f59e0b]/10 text-[#f59e0b] text-xs font-medium">
              <Video className="w-4 h-4" /> YouTube
            </button>
            <span className="text-xs text-[#6b7280]">Connected releases only</span>
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Title</span>
              <input className="input" maxLength={100} value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>

            <label className="block">
              <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Description</span>
              <textarea className="input" rows={8} value={description} onChange={(e) => setDescription(e.target.value)} />
            </label>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Visibility</span>
                <select className="input" value={visibility} onChange={(e) => setVisibility(e.target.value)}>
                  <option>Public</option>
                  <option>Unlisted</option>
                  <option>Private</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Audience</span>
                <select className="input" value={audience} onChange={(e) => setAudience(e.target.value)}>
                  <option>Not made for kids</option>
                  <option>Made for kids</option>
                </select>
              </label>
            </div>

            <label className="flex items-center justify-between p-3 rounded-lg bg-[#0f1117] border border-[#1e2130]">
              <div>
                <p className="text-xs font-medium text-[#f0f1f5]">Contains AI-generated material</p>
                <p className="text-[10px] text-[#6b7280]">Required for Atlas-generated releases.</p>
              </div>
              <input type="checkbox" checked disabled className="accent-[#f59e0b]" />
            </label>

            <div>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setScheduleMode("now")}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                    scheduleMode === "now" ? "bg-[#f59e0b]/10 text-[#f59e0b]" : "text-[#9ca3af] hover:bg-[#0f1117]"
                  )}
                >
                  Publish now
                </button>
                <button
                  onClick={() => setScheduleMode("schedule")}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                    scheduleMode === "schedule" ? "bg-[#f59e0b]/10 text-[#f59e0b]" : "text-[#9ca3af] hover:bg-[#0f1117]"
                  )}
                >
                  <Calendar className="w-3.5 h-3.5 inline mr-1" /> Schedule
                </button>
              </div>

              {scheduleMode === "schedule" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                  <label className="block">
                    <span className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Release date</span>
                    <input type="datetime-local" className="input" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
                  </label>
                </motion.div>
              )}
            </div>

            <button
              onClick={handlePublish}
              disabled={!ready || publishing || store.published}
              className="btn-primary w-full py-3"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : store.published ? "Release submitted ✓" : "Connect YouTube & publish"}
            </button>

            <p className="text-[10px] text-[#6b7280] text-center">Nothing goes live without this final confirmation. Build and validate a master before releasing it.</p>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <div className="surface p-4">
            <p className="text-xs font-medium text-[#9ca3af] mb-3">YouTube preview</p>
            <div className="space-y-3">
              <div className="aspect-video bg-[#0f1117] rounded-lg flex items-center justify-center relative">
                <span className="text-lg font-bold tracking-widest text-[#6b7280] uppercase">{store.title}</span>
                <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] text-white font-medium">3:42</span>
              </div>
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-[#f59e0b] flex items-center justify-center text-[#08090d] text-xs font-bold">A</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#f0f1f5] truncate">{title}</p>
                  <p className="text-xs text-[#6b7280]">Atlas by HillPrime Innovations · Release preview</p>
                </div>
              </div>
            </div>
          </div>

          <div className="surface p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center text-white">
                <Video className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-[#f0f1f5]">YouTube</p>
                <p className="text-xs text-[#6b7280]">
                  {!store.identity ? "Sign in to Atlas first" : !store.capabilities.youtube ? "YouTube publishing not connected" : store.youtubeConnected ? "YouTube connected" : "YouTube is ready to connect"}
                </p>
              </div>
              <button
                onClick={handleConnectVideo}
                disabled={!store.identity || !store.capabilities.youtube || store.youtubeConnected}
                className="btn-subtle text-xs px-2.5 py-1.5"
              >
                {store.youtubeConnected ? <Check className="w-3.5 h-3.5" /> : "Connect"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
